import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiX, FiPhone, FiMail, FiMapPin, FiUsers, FiPackage } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { supplierAPI, categoryAPI } from '../../services/api';

const SupplierManagement = () => {
  const { isMainAdmin } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' ou 'list'
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    country: '',
  });

  useEffect(() => {
    if (isMainAdmin) {
      fetchSuppliers();
      fetchCategories();
    }
  }, [isMainAdmin]);

  const fetchSuppliers = async () => {
    try {
      console.log('🔄 Fetching suppliers...');
      const response = await supplierAPI.getAll();
      console.log('🏢 Suppliers response:', response);
      
      // Gestion flexible de la réponse
      let suppliersData = [];
      
      if (response.data && response.data.success && response.data.data) {
        suppliersData = response.data.data.data;
      } else if (Array.isArray(response.data.data.data)) {
        suppliersData = response.data.data.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        suppliersData = response.data.data.data;
      }
      
      console.log('📦 Suppliers data:', suppliersData);
      setSuppliers(suppliersData);
    } catch (error) {
      console.error('❌ Erreur fetchSuppliers:', error);
      console.error('📋 Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      alert('Erreur lors du chargement des fournisseurs: ' + (error.response?.data?.message || error.message));
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getAll();
      console.log('📂 Categories response:', response);
      
      let categoriesData = [];
      
      if (response.data && response.data.success && response.data.data) {
        categoriesData = response.data.data.data;
      } else if (Array.isArray(response.data)) {
        categoriesData = response.data.data.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        categoriesData = response.data.data.data;
      }
      
      setCategories(categoriesData);
    } catch (error) {
      console.error('❌ Error fetching categories:', error);
      console.error('📋 Categories error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);

    try {
      const supplierData = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        city: formData.city,
        country: formData.country,
      };

      console.log('📤 Sending supplier data:', supplierData);

      if (editingSupplier) {
        const supplierId = editingSupplier.id_fournisseur || editingSupplier.id;
        await supplierAPI.update(supplierId, supplierData);
        alert('✅ Fournisseur modifié avec succès!');
      } else {
        await supplierAPI.create(supplierData);
        alert('✅ Fournisseur ajouté avec succès!');
      }
      
      await fetchSuppliers();
      closeModal();
    } catch (error) {
      console.error('❌ Erreur handleSubmit:', error);
      console.error('📋 Submit error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      alert('❌ Erreur lors de l\'opération: ' + (error.response?.data?.message || error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (supplier) => {
    console.log('✏️ Editing supplier:', supplier);
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      address: supplier.address || '',
      city: supplier.city || '',
      country: supplier.country || '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce fournisseur?')) {
      try {
        await supplierAPI.delete(id);
        alert('✅ Fournisseur supprimé avec succès!');
        await fetchSuppliers();
      } catch (error) {
        console.error('❌ Erreur handleDelete:', error);
        alert('❌ Erreur lors de la suppression: ' + (error.response?.data?.message || error.response?.data?.error || error.message));
      }
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingSupplier(null);
    setFormData({
      nom: '',
      numero: '',
      mail: '',
      adresse: '',
      city: '',
      country: '',
    });
  };

  // retire les accents pour une comparaison plus facile
  const removeAccents = (str) => {
    if (!str) return '';
    return str
        .normalize('NFD') // Décompose les caractères accentués
        .replace(/[\u0300-\u036f]/g, '') // Supprime les diacritiques
        .toLowerCase();
  };

  // Filtrage par nom, téléphone ou email
  const filteredSuppliers = suppliers.filter(supplier => {
    const supplierName = supplier.name || '';
    const supplierPhone = supplier.phone || '';
    const supplierEmail = supplier.email || '';
    const supplierCity = supplier.city || '';
    const supplierCountry = supplier.country || '';

    // Normaliser le terme de recherche
    const normalizedSearch = removeAccents(searchTerm);
    
    // Normaliser les valeurs individuelles
    const normalizedName = removeAccents(supplierName);
    const normalizedEmail = removeAccents(supplierEmail);
    const normalizedCity = removeAccents(supplierCity);
    const normalizedCountry = removeAccents(supplierCountry);
    
    // Pour le téléphone, pas besoin de removeAccents
    const phoneSearch = searchTerm;
    
    return (
        normalizedName.includes(normalizedSearch) ||
        supplierPhone.includes(phoneSearch) ||
        normalizedEmail.includes(normalizedSearch) ||
        normalizedCity.includes(normalizedSearch) ||
        normalizedCountry.includes(normalizedSearch)
    );
  });

  // Fonctions utilitaires pour obtenir les informations
  const getSupplierName = (supplier) => {
    return supplier.name || 'Sans nom';
  };

  const getSupplierPhone = (supplier) => {
    return supplier.phone || 'N/A';
  };

  const getSupplierEmail = (supplier) => {
    return supplier.email || 'N/A';
  };

  const getSupplierAddress = (supplier) => {
    return supplier.address || 'Adresse non fournie';
  };

  const getSupplierCity = (supplier) => {
    return supplier.city || 'Ville non fournie';
  };

  const getSupplierCountry = (supplier) => {
    return supplier.country || 'Pays non fourni';
  };

  const getSupplierCategories = (supplier) => {
    return supplier.categories_names || supplier.categories || [];
  };

  const getSupplierId = (supplier) => {
    return supplier.id || supplier.suppliers_id;
  };

  const getProductsCount = (supplier) => {
    return supplier.products?.length || 0;
  };

  // Si l'utilisateur n'est pas admin principal, afficher un message
  if (!isMainAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
        <FiUsers className="text-6xl text-gray-300 mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">Accès restreint</h2>
        <p className="text-gray-600 text-center max-w-md">
          La gestion des fournisseurs est réservée aux administrateurs principaux.
          <br />
          En tant que sous-admin, vous pouvez gérer les produits et les commandes.
        </p>
      </div>
    );
  }

  return (
    <div className="pb-6">
      {/* En-tête avec titre et actions */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Gestion des Fournisseurs</h1>
            <p className="text-gray-600 text-sm md:text-base mt-1">
              {filteredSuppliers.length} fournisseur{filteredSuppliers.length !== 1 ? 's' : ''} trouvé{filteredSuppliers.length !== 1 ? 's' : ''}
            </p>
          </div>
          
          <div className="flex flex-col xs:flex-row gap-3">
            {/* Bouton d'ajout */}
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold text-sm md:text-base whitespace-nowrap"
            >
              <FiPlus className="text-lg" />
              <span>Ajouter un fournisseur</span>
            </button>
          </div>
        </div>

        {/* Info permissions */}
        <div className="p-3 md:p-4 bg-green-50 rounded-lg border border-green-100">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <p className="text-sm md:text-base text-green-800 font-medium">
              👑 Admin Principal - Gestion complète des fournisseurs
            </p>
            <div className="flex gap-4 text-xs md:text-sm text-green-700">
              <span className="flex items-center gap-1">
                <FiUsers className="text-green-600" />
                <span>{suppliers.length} fournisseurs</span>
              </span>
              <span className="flex items-center gap-1">
                <FiPackage className="text-green-600" />
                <span>{categories.length} catégories</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Barre de recherche */}
      <div className="mb-6">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
          <input
            type="text"
            placeholder="Rechercher par nom, téléphone, email, ville..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm md:text-base"
          />
        </div>
      </div>

      {/* Grid des fournisseurs - 2 par ligne sur mobile */}
      <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
        {filteredSuppliers.map((supplier) => (
          <div 
            key={getSupplierId(supplier)} 
            className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 overflow-hidden flex flex-col h-full"
          >
            {/* En-tête avec nom et actions */}
            <div className="p-4 md:p-5 border-b border-gray-100">
              <div className="flex justify-between items-start gap-2">
                <h3 className="text-base md:text-lg font-bold text-gray-800 leading-tight truncate" title={getSupplierName(supplier)}>
                  {getSupplierName(supplier)}
                </h3>
                <div className="flex gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleEdit(supplier)}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    title="Modifier"
                  >
                    <FiEdit2 className="text-base md:text-lg" />
                  </button>
                  <button
                    onClick={() => handleDelete(getSupplierId(supplier))}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    title="Supprimer"
                  >
                    <FiTrash2 className="text-base md:text-lg" />
                  </button>
                </div>
              </div>
              
              {/* Badge de ville et pays */}
              <div className="flex items-center gap-2 mt-2">
                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full truncate max-w-[50%]">
                  {getSupplierCity(supplier)}
                </span>
                <span className="px-2 py-0.5 bg-gray-100 text-gray-800 text-xs font-semibold rounded-full truncate max-w-[50%]">
                  {getSupplierCountry(supplier)}
                </span>
              </div>
            </div>

            {/* Informations principales */}
            <div className="p-4 md:p-5 flex-grow">
              <div className="space-y-3">
                {/* Téléphone */}
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-green-100 rounded-lg flex-shrink-0">
                    <FiPhone className="text-green-600 text-sm" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-500 mb-0.5">Téléphone</p>
                    <p className="text-sm font-medium text-gray-800 truncate" title={getSupplierPhone(supplier)}>
                      {getSupplierPhone(supplier)}
                    </p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-blue-100 rounded-lg flex-shrink-0">
                    <FiMail className="text-blue-600 text-sm" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-500 mb-0.5">Email</p>
                    <p className="text-sm font-medium text-gray-800 truncate" title={getSupplierEmail(supplier)}>
                      {getSupplierEmail(supplier)}
                    </p>
                  </div>
                </div>

                {/* Adresse */}
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-orange-100 rounded-lg flex-shrink-0">
                    <FiMapPin className="text-orange-600 text-sm" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-500 mb-0.5">Adresse</p>
                    <p className="text-sm text-gray-700 line-clamp-2" title={getSupplierAddress(supplier)}>
                      {getSupplierAddress(supplier)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer avec statistiques */}
            <div className="p-4 md:p-5 border-t border-gray-100 bg-gray-50">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <FiPackage className="text-gray-400 text-sm" />
                  <span className="text-sm font-semibold text-gray-700">
                    {getProductsCount(supplier)} produit{getProductsCount(supplier) !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  ID: #{getSupplierId(supplier)?.toString().substring(0, 6)}
                </div>
              </div>
              
              {/* Catégories */}
              {getSupplierCategories(supplier).length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-1.5">Catégories</p>
                  <div className="flex flex-wrap gap-1">
                    {getSupplierCategories(supplier).slice(0, 2).map((cat, index) => (
                      <span
                        key={index}
                        className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-green-100 text-green-800 truncate max-w-[100px]"
                        title={typeof cat === 'object' ? cat.name : cat}
                      >
                        {typeof cat === 'object' ? cat.name : cat}
                      </span>
                    ))}
                    {getSupplierCategories(supplier).length > 2 && (
                      <span className="px-1.5 py-0.5 text-[10px] text-gray-500">
                        +{getSupplierCategories(supplier).length - 2}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* États vides */}
      {filteredSuppliers.length === 0 && suppliers.length > 0 && (
        <div className="text-center py-12 md:py-16">
          <FiSearch className="text-5xl md:text-6xl text-gray-300 mx-auto mb-4" />
          <p className="text-lg md:text-xl text-gray-500 font-medium">Aucun fournisseur trouvé</p>
          <p className="text-gray-400 text-sm md:text-base mt-2">
            Essayez avec d'autres termes de recherche
          </p>
          <button 
            onClick={() => setSearchTerm('')}
            className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm md:text-base"
          >
            Réinitialiser la recherche
          </button>
        </div>
      )}

      {suppliers.length === 0 && (
        <div className="text-center py-12 md:py-16">
          <FiUsers className="text-5xl md:text-6xl text-gray-300 mx-auto mb-4" />
          <p className="text-lg md:text-xl text-gray-500 font-medium">Aucun fournisseur enregistré</p>
          <p className="text-gray-400 text-sm md:text-base mt-2 mb-6">
            Commencez par ajouter votre premier fournisseur
          </p>
          <button 
            onClick={() => setIsModalOpen(true)}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-sm md:text-base"
          >
            Ajouter le premier fournisseur
          </button>
        </div>
      )}

      {/* Modal d'ajout/modification */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 md:p-6 border-b sticky top-0 bg-white">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                {editingSupplier ? 'Modifier le fournisseur' : 'Ajouter un fournisseur'}
              </h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-full">
                <FiX className="text-xl md:text-2xl text-gray-600" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4 md:space-y-6">
              {/* Nom du fournisseur */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nom du fournisseur *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm md:text-base"
                  placeholder="Ex: TechStore CM"
                />
              </div>

              {/* Téléphone et Email en ligne sur desktop */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm md:text-base"
                    placeholder="Ex: +237 6XX XX XX XX"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm md:text-base"
                    placeholder="Ex: contact@techstore.cm"
                  />
                </div>
              </div>

              {/* Ville et Pays en ligne sur desktop */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Ville *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm md:text-base"
                    placeholder="Ex: Douala"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Pays *
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm md:text-base"
                    placeholder="Ex: Cameroun"
                  />
                </div>
              </div>

              {/* Adresse */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Adresse complète
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm md:text-base resize-none"
                  placeholder="Ex: 123 Rue du Commerce, Bonanjo"
                />
              </div>

              {/* Boutons d'action */}
              <div className="flex flex-col xs:flex-row gap-3 pt-4 md:pt-6 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 md:px-6 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-colors text-sm md:text-base"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`flex-1 px-4 md:px-6 py-3 rounded-lg font-semibold transition-colors text-sm md:text-base ${
                    loading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {loading ? 'Enregistrement...' : editingSupplier ? 'Modifier' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierManagement;